import React, { useState, useRef, useEffect } from 'react';
import injectScript from '../utils/document/inject-script';

import { injectMetadata } from '../utils/media';

import RecordRTC from 'recordrtc';
import { UsedRTCMediaDevices, stopStream } from './helpers';
import { FormikCheckbox } from 'src/common/form/formik/checkbox';

import { RecordSettings } from './settings';
import { ToggleComponent } from 'src/common/form/toggle/component';

// this is a workaround for webrtc blob durations
injectScript('https://cdn.webrtc-experiment.com/EBML.js');

export default function RTCCamera(props) {
  const [errorMessage, setErrorMessage] = useState(null);
  const [videoBlob, setVideoBlob] = useState(null);
  const [videoInputs, setVideoInputs] = useState([]);
  const [audioInputs, setAudioInputs] = useState([]);
  const [recording, setRecording] = useState(false);
  const [durationElapsed, setDurationElapsed] = useState(null);
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const recorderRef = useRef(null);
  const {
    onDurationElapsedUpdate,
    onRecordingStatusChange,
    onOptimizeFullscreenMobileChange,
    optimizeFullscreenMobile,
  } = props;
  // const COUNTDOWN_FROM = 3000;

  let looperId = useRef(null);
  let countdownId = useRef(null);

  const [selectedVideoInput, setSelectedVideoInput] = useState(null);
  const [selectedAudioInput, setSelectedAudioInput] = useState(null);
  let rtcVideoEl = useRef(null);

  let videoEl = props.videoEl || rtcVideoEl;

  useEffect(() => {
    initiateCameraRecorder();

    return () => {
      // cleanup aka componentWillUnmount
      if (recorderRef.current) {
        stopStream(recorderRef.current.stream);
        recorderRef.current.destroy();
        recorderRef.current = null;

        props.onPreviewStream && props.onPreviewStream(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onRecordingStatusChange && onRecordingStatusChange(recording);
  }, [recording, onRecordingStatusChange]);

  useEffect(() => {
    if (onDurationElapsedUpdate) {
      onDurationElapsedUpdate(durationElapsed);
    }
  }, [durationElapsed, onDurationElapsedUpdate]);

  const mediaDevicesDevError = `Media API doesn't work with insecure origins. See the README.md for marking this domain as a secure origin.`;

  const getUserMedia = (constraints) => {
    if (navigator.mediaDevices?.getUserMedia) {
      return navigator.mediaDevices.getUserMedia(constraints);
    } else if (navigator.getUserMedia) {
      return navigator.getUserMedia(constraints);
    } else {
      let msg = 'Your browser does NOT supports getUserMedia API.';
      if (process.env.NODE_ENV === 'development') {
        msg = mediaDevicesDevError;
      }

      setErrorMessage(msg);
      throw new Error(msg);
    }
  };

  const getAvailableDevices = () => {
    if (navigator.mediaDevices?.enumerateDevices) {
      return navigator.mediaDevices.enumerateDevices();
    } else if (navigator.enumerateDevices) {
      return navigator.enumerateDevices();
    } else {
      let msg = 'Your browser does NOT supports getUserMedia API.';
      if (process.env.NODE_ENV === 'development') {
        msg = mediaDevicesDevError;
      }

      setErrorMessage(msg);
      throw new Error(msg);
    }
  };

  const getStream = async (deviceIds = {}) => {
    if (recorderRef.current && recorderRef.current.stream) {
      stopStream(recorderRef.current.stream);
    }

    const constraints = {
      audio: { deviceId: deviceIds.audio ? { exact: deviceIds.audio } : undefined },
      video: {
        deviceId: deviceIds.video ? { exact: deviceIds.video } : undefined,
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
    };

    try {
      const stream = await getUserMedia(constraints);
      props.onPreviewStream && props.onPreviewStream(stream);
      gotStream(stream);
      return stream;
    } catch (error) {
      console.error(error);
      setErrorMessage('Unable to capture your camera');
    }
  };

  const gotDevices = (devices, stream) => {
    let videoDevices = [];
    let audioDevices = [];

    devices.forEach((device) => {
      const kind = device.kind;
      if (kind === 'audioinput') {
        audioDevices.push(device);
      } else if (kind === 'videoinput') {
        videoDevices.push(device);
      }
    });

    setVideoInputs(videoDevices);
    setAudioInputs(audioDevices);

    // find selected audio and video options from the stream itself
    setSelectedAudioInput(audioDevices.find((option) => option.label === stream.getAudioTracks()[0].label));
    setSelectedVideoInput(videoDevices.find((option) => option.label === stream.getVideoTracks()[0].label));
  };

  const initiateCameraRecorder = async () => {
    // Fetch available devices before calling `getUserMedia` so that
    // we can just get the device IDs
    // (note: the label values won't be available if we don't already have media permissions)
    const availableDevices = await getAvailableDevices();
    const availableDeviceIdList = availableDevices.map((d) => d.deviceId);

    // Get previously used deviceId's and check whether they are currently available
    // Case: user might have used a device that is no longer available
    const usedDevices = UsedRTCMediaDevices.get();
    if (usedDevices.video && !availableDeviceIdList.includes(usedDevices.video)) {
      usedDevices.video = undefined;
    }
    if (usedDevices.audio && !availableDeviceIdList.includes(usedDevices.audio)) {
      usedDevices.audio = undefined;
    }

    // Attempt to get the stream
    const stream = await getStream(usedDevices);
    if (stream) {
      // Proceed only if user permissions were received and stream was received
      // Get available device list again in case we don't have the complete
      // data with labels
      const updatedAvailableDevices = await getAvailableDevices();
      gotDevices(updatedAvailableDevices, stream);
    }
  };

  const handleDeviceChange = (newDevice) => {
    const kind = newDevice.kind;

    // Pick out the deviceId's in use
    const deviceIdsInUse = {
      audio: kind === 'audioinput' ? newDevice.deviceId : selectedAudioInput?.deviceId,
      video: kind === 'videoinput' ? newDevice.deviceId : selectedVideoInput?.deviceId,
    };

    // Create stream with the deviceId's and save it in localStorage
    getStream(deviceIdsInUse);
    UsedRTCMediaDevices.set(deviceIdsInUse);
  };

  const gotStream = (stream) => {
    if (videoEl.current) {
      videoEl.current.removeAttribute('src');
      videoEl.current.muted = true;
      videoEl.current.volume = 0;
      videoEl.current.controls = false;
      videoEl.current.autoPlay = true;
      videoEl.current.srcObject = stream;
      videoEl.current.load();
    }

    recorderRef.current = RecordRTC(stream, {
      type: 'video',
      mimeType: 'video/webm',
    });

    // release the strean on stopRecording
    recorderRef.current.stream = stream;

    props.onPreviewStream && props.onPreviewStream(stream);

    return stream;
  };

  const startRecordingCamera = () => {
    let counter = 3;
    setCountdown(counter);
    setShowCountdown(true);

    countdownId.current = setInterval(() => {
      counter -= 1;
      setCountdown(counter);

      if (counter < 0) {
        // for some reason if the counter drops 0, then clear countdown interval
        clearInterval(countdownId.current);
      }

      if (counter === 0) {
        clearInterval(countdownId.current);
        setShowCountdown(false);
        recorderRef.current && recorderRef.current.startRecording();

        // start duration timer
        const dateStarted = new Date().getTime();
        looperId.current = setInterval(() => {
          setDurationElapsed((new Date().getTime() - dateStarted) / 1000);
        }, 1000);

        setRecording(true);
      }
    }, 1000);
  };

  const stopRecordingCamera = () => {
    clearInterval(looperId.current);
    clearInterval(countdownId.current);
    recorderRef.current &&
      recorderRef.current.stopRecording &&
      recorderRef.current.stopRecording(stopRecordingCameraCallback);
  };

  const resetCamera = async () => {
    clearInterval(looperId.current);
    clearInterval(countdownId.current);
    setShowCountdown(true);
    setVideoBlob(null);
    setRecording(false);
    setDurationElapsed(null);

    if (recorderRef.current && recorderRef.current.stream) {
      stopStream(recorderRef.current.stream);
    }

    if (props.onComplete) {
      // reset the completed file if set before
      props.onComplete(null);
    }

    // get the stream again
    await initiateCameraRecorder();
    startRecordingCamera();
  };

  const stopRecordingCameraCallback = () => {
    if (recorderRef.current) {
      if (videoEl.current) {
        videoEl.current.src = null;
        videoEl.current.srcObject = null;
        videoEl.current.muted = false;
        videoEl.current.volume = 1;
        videoEl.current.controls = true;
      }

      const completeRecording = (seekableBlob) => {
        if (videoEl.current) {
          videoEl.current.src = URL.createObjectURL(seekableBlob);
        }

        setVideoBlob(seekableBlob);

        // release stream and destroy recorder
        if (recorderRef.current) {
          stopStream(recorderRef.current.stream);
          recorderRef.current.destroy();
        }

        // send recorded duration with onComplete
        const recordedDuration = durationElapsed;

        // revert states back to initial
        recorderRef.current = null;
        setRecording(false);
        setDurationElapsed(null);
        clearInterval(looperId.current);
        clearInterval(countdownId.current);

        // send the file up to video node form
        if (props.onComplete) {
          // Generate a randon name uuid
          const currentDate = new Date();
          const tmpFile = new File(
            [seekableBlob],
            `recording-${currentDate.toLocaleDateString()}-${currentDate.toLocaleTimeString()}`,
            {
              type: 'video/webm',
            }
          );
          props.onComplete(tmpFile, recordedDuration);
        }
      };

      // RecordRTC does not handle duration calculation by default
      const recordedBlob = recorderRef.current.getBlob();
      injectMetadata(recordedBlob)
        .then((seekableBlob) => {
          completeRecording(seekableBlob);
        })
        .catch((err) => {
          completeRecording(recordedBlob);
        });
    }
  };

  const renderCountdown = () => {
    if (!showCountdown || countdown <= 0) {
      return null;
    }

    return (
      <div className='fixed top-0 bottom-0 left-0 right-0 !my-0 items-center justify-center flex m-0 p-0 z-50 w-full h-full bg-bedrock-black/backdrop'>
        <div className='font-brand-serif text-bedrock-white font-normal	text-[120px] leading-[120px]'>{countdown}</div>
      </div>
    );
  };

  if (errorMessage) {
    return <div className='flex text-primary'>{errorMessage}</div>;
  }

  const recordingEnabled = audioInputs.length > 0 || videoInputs.length > 0;

  return (
    <>
      <div className='flex flex-col space-y-6'>
        {
          <div className='flex flex-col space-y-2'>
            {!props.hideOptimizeForMobileOption && (
              <div className='flex flex-col space-y-1'>
                <FormikCheckbox
                  id='is_mobile_optimized_checkbox'
                  name='is_mobile_optimized'
                  label='Optimize for full screen mobile'
                  labelClasses='font-normal text-secondary'
                />
                <label htmlFor='is_mobile_optimized_checkbox' className='text-xs text-gray-500'>
                  If enabled, videos will be displayed vertically on mobile.
                </label>
              </div>
            )}
            {!recording && (
              <button
                type='button'
                disabled={!recordingEnabled || recording || showCountdown || videoBlob}
                className='btn-bedrock-primary'
                onClick={startRecordingCamera}
              >
                Record
              </button>
            )}
            {recording && (
              <>
                <button type='button' disabled={!recording} className='btn-bedrock-red' onClick={stopRecordingCamera}>
                  <div className='text-white'>Stop Recording</div>
                </button>
                <button type='button' className='btn-bedrock-secondary' disabled={showCountdown} onClick={resetCamera}>
                  Restart
                </button>
              </>
            )}
            {!recording && !showCountdown && props.loomButton && props.loomButton}
          </div>
        }
      </div>
      {props.videoEl === undefined && <video autoPlay muted playsInline ref={videoEl}></video>}
      <div className='h-px bg-bedrock-gray-medium w-full rounded-[100px]' />
      <div className='flex flex-col space-y-md'>
        <RecordSettings
          audioInputs={audioInputs}
          videoInputs={videoInputs}
          selectedAudioInput={selectedAudioInput}
          selectedVideoInput={selectedVideoInput}
          onDeviceChange={handleDeviceChange}
        />
        <ToggleComponent
          checked={optimizeFullscreenMobile}
          onChange={onOptimizeFullscreenMobileChange}
          label='Optimize for fullscreen mobile'
          labelType='strong'
        />
      </div>
      {renderCountdown()}
    </>
  );
}
